/**
 * Same-origin build files (`/assets/…`) worth checking after a chunk fails to
 * load: those named in the error message (Chrome and Firefox name the module;
 * Safari names nothing) plus every other build file this page already pulled in,
 * since a failing chunk can be a healthy file whose dependency is the bad one.
 */
export function assetCandidates(message: string, pageUrls: readonly string[], origin: string): string[] {
  const named = message.match(/(?:https?:\/\/[^\s'"()<>]+|\/assets\/[^\s'"()<>]+)/g) ?? [];
  const out = new Set<string>();
  for (const raw of [...named, ...pageUrls]) {
    let url: URL;
    try {
      url = new URL(raw, origin);
    } catch {
      continue;
    }
    if (url.origin === origin && url.pathname.startsWith("/assets/")) out.add(url.origin + url.pathname);
  }
  return [...out];
}

/**
 * Whether the browser's cached copy of a build file is not that file. Probed with
 * `cache: "only-if-cached"`, which answers 504 when nothing is cached — that is
 * healthy, the next load goes to the network. Anything else that is an error, or
 * is HTML (the SPA shell served under the chunk's name), is a poisoned entry that
 * an ordinary reload will keep reading back for the rest of its `immutable` year.
 */
export function isPoisonedCacheEntry(status: number, contentType: string | null): boolean {
  if (status === 504) return false;
  if (status < 200 || status >= 300) return true;
  return (contentType ?? "").startsWith("text/html");
}
